/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { authFetch } from '@opengeoweb/authentication';
import { debugLogger, DebugType, isDefined } from './WMJSTools';

export interface WMHeader {
  name: string;
  value: string;
}

export interface WMImageOptions {
  randomizer?: boolean;
  headers: WMHeader[];
  fakeMaxAge?: number;
}

export interface WMGeoReference {
  extent: number[];
  resolution: number;
}

/**
 * Returns the current time in ms
 * @returns the current time in ms
 */
export const getCurrentImageTime = (): number => {
  return new Date().getTime();
};

// A failed image becomes stale after this delay, so it is retried. The delay doubles with every failed attempt, up to the max.
export const WMIMAGE_ERROR_RETRY_DELAY_MS = 10000;
export const WMIMAGE_ERROR_RETRY_MAX_DELAY_MS = 300000;

/**
 * WMImage provides an API to the HTML image element. It is used for caching and easier access to images.
 */
export default class WMImage {
  imageLife!: number;

  randomize!: boolean;

  _srcLoaded!: string;

  _isLoaded!: boolean;

  _isLoading!: boolean;

  _hasError!: boolean;

  srcToLoad!: string;

  loadEventCallback?: (image: WMImage) => void;

  el!: HTMLImageElement;

  headers!: WMHeader[];

  _imageTimeStampAtError!: number;

  _numFailedAttempts!: number;

  _loadStartTime: number;
  _loadDuration?: number;

  // Maximum age, in seconds, parsed from Cache-Control response header.
  _maxAgeSeconds?: number | null;
  _loadedAtTime?: number;
  _objectUrl?: string;
  _resolveList: ((i: WMImage) => void)[] = [];
  geoReference: WMGeoReference | null = null;
  hashUrl: string | undefined;
  setGeoReference(geoReference: WMGeoReference): void {
    if (this.geoReference) {
      return;
    }
    this.geoReference = {
      extent: [...geoReference.extent],
      resolution: geoReference.resolution,
    };
  }
  /**
   *
   * @param src Optionally set the source URL to load
   * @param callback Optionally set the callback function to trigger once image has loaded
   * @param options Additional options to the image. You can set { randomizer: true } to force unique url's and prevent caching.
   */
  constructor(
    src: string,
    callback?: (image: WMImage) => void,
    options?: WMImageOptions,
  ) {
    this._loadDuration = undefined;
    this._loadStartTime = 0;
    this.randomize = false;
    this._srcLoaded = undefined!;
    this._isLoaded = undefined!;
    this._isLoading = undefined!;
    this._hasError = undefined!;
    this._imageTimeStampAtError = getCurrentImageTime();
    this._numFailedAttempts = 0;
    this.srcToLoad = src;
    this.loadEventCallback = callback;
    this.el = new Image();
    // TODO - We need to build a fetching mechanism that will handle both cases of fetching images with CORS headers of "Access-Control-Allow-Origin: *" and without - this.el.crossOrigin = 'anonymous';
    this.headers = [];
    if (isDefined(options) && isDefined(options!.randomizer)) {
      this.randomize = options!.randomizer!;
    }

    this.init = this.init.bind(this);
    this.isLoaded = this.isLoaded.bind(this);
    this.isLoading = this.isLoading.bind(this);
    this.setSource = this.setSource.bind(this);
    this.clear = this.clear.bind(this);
    this.getSrc = this.getSrc.bind(this);
    this.hasError = this.hasError.bind(this);
    this._load = this._load.bind(this);
    this.load = this.load.bind(this);
    this.forceReload = this.forceReload.bind(this);
    this.getLastErrorMSecondsAgo = this.getLastErrorMSecondsAgo.bind(this);
    this.getNumFailedAttempts = this.getNumFailedAttempts.bind(this);
    this.isStale = this.isStale.bind(this);

    this._loadEvent = this._loadEvent.bind(this);
    this.getWidth = this.getWidth.bind(this);
    this.getHeight = this.getHeight.bind(this);
    this.getElement = this.getElement.bind(this);
    this._getImageWithHeaders = this._getImageWithHeaders.bind(this);
    this.init();

    this.el.addEventListener('load', () => {
      this._loadEvent(false);
    });
    this.el.addEventListener('error', () => {
      this._loadEvent(true);
    });
    this.el.onselectstart = (): boolean => {
      return false;
    };
    this.el.ondrag = (): boolean => {
      return false;
    };
  }

  private init(): void {
    this._srcLoaded = 'undefined image';
    this._isLoaded = false;
    this._isLoading = false;
    this._hasError = false;
    this._numFailedAttempts = 0;
    this._imageTimeStampAtError = getCurrentImageTime();
    this._maxAgeSeconds = undefined;
    this._loadedAtTime = undefined;
  }

  /**
   * Returns true if the image has been loaded
   */
  isLoaded(): boolean {
    if (this._isLoading) {
      return false;
    }
    if (!this._isLoaded) {
      return false;
    }
    return !this.isStale();
  }

  isLoadedWithoutErrors(): boolean {
    return this.isLoaded() && !this.hasError();
  }

  hasNotStartedLoading(): boolean {
    return !this._isLoaded && !this._isLoading;
  }

  isStale(): boolean {
    if (!this._isLoaded) {
      return false;
    }
    if (this._hasError) {
      // Retry failed images after a delay, otherwise a temporary server error would hide the image permanently
      return this.getLastErrorMSecondsAgo() >= this.getErrorRetryDelay();
    }
    if (this._loadedAtTime === undefined) {
      return false;
    }
    if (this._maxAgeSeconds === null || this._maxAgeSeconds === undefined) {
      return false;
    }
    const imageAge = getCurrentImageTime() - this._loadedAtTime;
    const maxAge = this._maxAgeSeconds * 1000;
    return imageAge > maxAge;
  }

  /**
   * Returns true if the image is still loading
   */
  public isLoading(): boolean {
    return this._isLoading;
  }

  /**
   * Get the amount of milliseconds since the image experienced an image load.
   * @returns milliseconds since the image had an error
   */
  public getLastErrorMSecondsAgo(): number {
    return getCurrentImageTime() - this._imageTimeStampAtError;
  }

  /**
   * @returns The number of failed attempts for this image
   */
  public getNumFailedAttempts(): number {
    return this._numFailedAttempts;
  }

  /**
   * @returns The amount of milliseconds after which a failed image becomes stale and should be retried
   */
  public getErrorRetryDelay(): number {
    const numRetries = Math.max(this._numFailedAttempts - 1, 0);
    return Math.min(
      WMIMAGE_ERROR_RETRY_DELAY_MS * 2 ** numRetries,
      WMIMAGE_ERROR_RETRY_MAX_DELAY_MS,
    );
  }

  /**
   * Set source of image, but it will not load the image yet.
   * @param src URL of the image to load
   * @param options WMImageOptions to give to the image
   */
  public setSource(src: string, options: WMImageOptions): void {
    if (this._isLoading) {
      debugLogger(
        DebugType.Error,
        '-------------------------> Source set while still loading!!! ',
      );
      return;
    }
    this.srcToLoad = src;
    if (options?.headers && options.headers.length > 0) {
      this.headers = options.headers;
    }
    if (this._srcLoaded === this.srcToLoad) {
      this._isLoaded = true;
      return;
    }
    this._numFailedAttempts = 0;
    this._imageTimeStampAtError = getCurrentImageTime();
    this._isLoaded = false;
  }

  /**
   * Re-intialize the image
   */
  public clear(): void {
    this.init();
  }

  /**
   * Get the URL of the image
   */
  public getSrc(): string {
    return this.srcToLoad;
  }

  /**
   * Check if the image has an error
   */
  public hasError(): boolean {
    return this._hasError;
  }

  public clearError(): void {
    if (this._hasError && !this._isLoading) {
      this._isLoaded = false;
      this._srcLoaded = undefined!;
    }
  }

  private _resolveAll(): void {
    while (this._resolveList.length !== 0) {
      const resolve = this._resolveList.pop();
      resolve?.(this);
    }
  }

  /**
   * Start loading the image
   */
  public async loadAwait(): Promise<WMImage> {
    return new Promise((resolve: (image: WMImage) => void) => {
      this._resolveList.push(resolve);
      this.load();
    });
  }

  public load(): void {
    if (!this._isLoading) {
      this._load();
    }
  }

  public forceReload(randomize = false): void {
    this._isLoaded = false;
    this._srcLoaded = undefined!;
    if (randomize) {
      this.randomize = true;
    }
    this._load();
  }

  private static _parseMaxAgeFromCacheControl(
    cacheControlHeader: string | null,
  ): number | null {
    if (!cacheControlHeader) {
      return null;
    }
    const maxAgeMatch = /(?:^|,)\s*max-age=(\d+)/i.exec(cacheControlHeader);
    if (!maxAgeMatch) {
      return null;
    }
    const parsed = Number(maxAgeMatch[1]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private _setMaxAgeFromResponseHeaders(response: Response): void {
    this._maxAgeSeconds = WMImage._parseMaxAgeFromCacheControl(
      response.headers.get('cache-control'),
    );
  }

  _getImageWithHeaders(url: string, headers: WMHeader[]): void {
    const fetchHeaders = new Headers();
    if (headers && headers.length > 0) {
      for (const header of headers) {
        fetchHeaders.append(header.name, header.value);
      }
    }
    const options: RequestInit = {
      method: 'GET',
      headers: fetchHeaders,
      mode: 'cors',
      cache: 'default',
    };

    const request = new Request(url);

    authFetch(request, options)
      .then((response) => {
        if (!response.ok) {
          void response.body?.cancel();
          this._loadEvent(true);
          return;
        }
        this._setMaxAgeFromResponseHeaders(response);
        response
          .blob()
          .then((imageBlob) => {
            this._revokeObjectUrl();
            this._objectUrl = URL.createObjectURL(imageBlob);
            this.el.src = this._objectUrl;
          })
          .catch(() => {
            // Do not log errors, as they are not very useful and can better be inspected via the network console
            this._loadEvent(true);
          });
      })
      .catch(() => {
        // Do not log errors, as they are not very useful and can better be inspected via the network consoles
        this._loadEvent(true);
      });
  }

  _load(): void {
    this._loadStartTime = getCurrentImageTime();

    if (this._isLoaded) {
      if (!this._hasError && !this.isStale()) {
        this._loadEvent(false);
        return;
      }
      this._isLoaded = false;
      this._srcLoaded = undefined!;
    }
    this._hasError = false;
    this._isLoading = true;
    if (!this.srcToLoad) {
      this._loadEvent(true);
      return;
    }

    /* Allow relative URL's */
    if (this.srcToLoad.startsWith('/') && !this.srcToLoad.startsWith('//')) {
      const splittedHREF = window.location.href
        .split('/')
        .filter((e) => e.length > 0);
      const hostName = `${splittedHREF[0]}//${splittedHREF[1]}/`;
      this.srcToLoad = hostName + this.srcToLoad;
    }

    /*
     * If the image has already loaded this source succesfully (without error), simply trigger the loadevent.
     */
    if (this.srcToLoad === this._srcLoaded && !this._hasError) {
      this._loadEvent(false);
      return;
    }

    /* Get an image which needs headers */
    this._getImageWithHeaders(this.srcToLoad, this.headers);
  }

  _loadEvent(hasError: boolean): void {
    this._loadDuration = hasError
      ? undefined
      : getCurrentImageTime() - this._loadStartTime;
    if (hasError) {
      this._imageTimeStampAtError = getCurrentImageTime();
      this._numFailedAttempts += 1;
    } else {
      this._numFailedAttempts = 0;
    }
    this._hasError = hasError;
    this._isLoading = false;
    this._isLoaded = true;
    this._loadedAtTime = hasError ? undefined : getCurrentImageTime();
    this._srcLoaded = this.srcToLoad;
    this._resolveAll();

    this.loadEventCallback?.(this);
    // Free up memory
    this._revokeObjectUrl();
  }

  private _revokeObjectUrl(): void {
    if (this._objectUrl) {
      URL.revokeObjectURL(this._objectUrl);
      this._objectUrl = undefined;
    }
  }

  public getLoadDuration(): number | undefined {
    return this._loadDuration;
  }

  /**
   * Get the width of the current image
   */
  public getWidth(): number {
    return this.el.width;
  }

  /**
   * Get the height of the current image
   */
  public getHeight(): number {
    return this.el.height;
  }

  /**
   * Get the HTMLImageElement behind the WMImage
   */
  public getElement(): HTMLImageElement {
    return this.el;
  }
}
